import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Icon } from 'common-components';
import {
    ProfilePictureActions,
    ProfilePictureInnerActions,
    UploadProfilePictureProps
} from './UploadProfilePictureTypes';
import { IconButton } from '../uiLibraryImports';
import styles from './uploadProfilePicture.module.scss';

const UploadProfilePicture: FC<UploadProfilePictureProps> = (props: UploadProfilePictureProps) => {
    const { imageSrc, onUploadImage, actions, imgSrcUrl, isLarge } = props;
    const [hover, setHover] = useState<boolean>(false);
    const [showActions, setShowActions] = useState<boolean>(false);
    const fileInput = useRef<any>(null);
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const src = imageSrc ? URL.createObjectURL(imageSrc) : imgSrcUrl;

    const handleFileInput = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (!e.target.files) return;
        const file = e.target.files[0];
        onUploadImage(file, file.name);
    };

    const handleOnClick = () => {
        if (actions) {
            setShowActions(true);
        } else {
            fileInput?.current?.click();
        }
    };

    const handleActionOnClick = (action: ProfilePictureActions) => {
        if (action.actionKey === ProfilePictureInnerActions.REPLACE) {
            fileInput?.current?.click();
        }
        if (action.actionKey === ProfilePictureInnerActions.DELETE) {
            action.action?.(true);
        }
        setShowActions(false);
    };

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (showActions && ref.current && !ref.current.contains(e.target)) {
                setShowActions(false);
            }
        };

        document.addEventListener('mousedown', checkIfClickedOutside);

        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [showActions]);

    return (
        <div className={styles['upload-image']}>
            {(imageSrc || imgSrcUrl) && !actions && (
                <img
                    src={src}
                    className={classnames(styles['image-circle-picture'], {
                        [styles['large-image-circle']]: isLarge
                    })}
                    alt="profile"
                />
            )}

            <label htmlFor="icon-button-file">
                {(!(imageSrc || imgSrcUrl) || actions) && (
                    <IconButton
                        onMouseEnter={() => {
                            setHover(true);
                        }}
                        onMouseLeave={() => {
                            setHover(false);
                        }}
                        onClick={handleOnClick}
                        aria-label="upload picture"
                        component="span"
                        size="medium"
                    >
                        <div className={styles['upload-icon-wrapper']}>
                            <img
                                src={actions ? src : `${process.env.PUBLIC_URL}/images/defaultProfilePicture.svg`}
                                className={classnames(styles['image-circle-picture'], {
                                    [styles['large-image-circle']]: isLarge
                                })}
                                alt="profile"
                            />
                            {(hover || showActions) && (
                                <>
                                    <Icon
                                        name="camera"
                                        key="camera"
                                        className={classnames(styles.camera, { [styles['large-camera']]: isLarge })}
                                    />
                                    {!showActions && (
                                        <Icon
                                            name="pinkPlus"
                                            key="pinkPlus"
                                            className={classnames(styles['plus-icon'], {
                                                [styles['large-plus-icon']]: isLarge
                                            })}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </IconButton>
                )}
                {actions && showActions && (
                    <div className={styles['actions-wrapper']} ref={ref}>
                        {actions.map((action, i) => (
                            <div className={styles.action} onClick={() => handleActionOnClick(action)} key={i}>
                                <Icon name={action.icon} className={styles.icon} />
                                <div className={styles['action-text']}>{action.title}</div>
                            </div>
                        ))}
                    </div>
                )}

                <input
                    accept="image/*"
                    ref={fileInput}
                    style={{ display: 'none' }}
                    id="upload-file"
                    type="file"
                    onChange={handleFileInput}
                />
            </label>
        </div>
    );
};

export default UploadProfilePicture;
